import { Popover, Table } from "antd";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { ApiService } from "../api";
import ApprovalButtonsColumn from "./ApprovalButtons";
import { FileDoneOutlined } from "@ant-design/icons";

type Props = {
  requestId?: string;
  workflowId: number;
  fetch: number;
  setFetch: (fetch: number) => void;
  // Passed straight through to the approval buttons, which offer the earlier
  // stages as return-for-correction targets.
  stages?: any[];
  currentStageKey?: string;
};

export const ViewApprovers: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const loadApprovers = async () => {
    setLoading(true);
    try {
      const users = await ApiService.apiMainRequestApproversGetApproversList(
        props.requestId!,
        props.workflowId
      );
      setData(users);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = (record: any) => {
    const content = <img src={record.ehs_sign} />;

    if (record.type === "EHS" && record.status === "Approved") {
      return (
        <div>
          {record.status} &nbsp;
          <Popover content={content}>
            <FileDoneOutlined />
          </Popover>
        </div>
      );
    } else {
      return <div>{record.status}</div>;
    }
  };

  useEffect(() => {
    loadApprovers();
  }, [props.requestId, props.workflowId, props.fetch]);

  // Only shown when the stage defines named roles, so legacy stages do not grow
  // an empty column.
  const hasRoles = data.some((row) => row.role_title);

  return (
    <Table
      dataSource={data}
      // Approving or returning a permit reloads this table via `fetch`; without
      // the spinner the row just sat there looking unchanged.
      loading={loading}
      // A user can legitimately appear twice in one stage (e.g. picked for one
      // slot and a member of another slot's pool), so the id alone is not unique.
      rowKey={(record: any) => `${record.user_id}-${record.role_id ?? ""}`}
    >
      {hasRoles ? (
        // Role titles come from the template (WorkflowRole.title), so they are
        // translated the same way step titles are: by their English source
        // string, falling back to it when a template defines its own wording.
        <Table.Column
          dataIndex={"role_title"}
          title={t("Role")}
          render={(value: string) => (value ? t(value) : value)}
        />
      ) : null}
      <Table.Column dataIndex={"user_fullname"} title={t("User")} />
      <Table.Column
        render={(record: any) => renderStatus(record)}
        title={t("Status")}
      />
      <Table.Column
        render={(record: any) => (
          <ApprovalButtonsColumn
            record={record}
            requestId={props.requestId}
            workflowId={props.workflowId}
            fetch={props.fetch}
            setFetch={props.setFetch}
            stages={props.stages}
            currentStageKey={props.currentStageKey}
          />
        )}
      />
    </Table>
  );
};
